import React from 'react'
import { Button, Modal } from '@climate/components'

interface VendorAgreementModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

export default function VendorAgreementModal({ isOpen, setIsOpen }: VendorAgreementModalProps) {
  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} wrapClasses="sm:max-w-7xl">
      <div>
        <h1 className="text-2xl font-bold mb-4">
          Tellus Climate Suite Vendor Agreement
        </h1>

        <p className="mb-4">
          This Vendor Agreement (&quot;Agreement&quot;) is entered into by the
          organization (&quot;Vendor&quot; or &quot;you&quot;) which has been invited to use the
          Tellus Sustain vendor portal (the &quot;Vendor Portal&quot; or the &quot;Services&quot;)
          operated by Tellus Sustainability Solutions LLC (&quot;we&quot; or &quot;us&quot;) by a
          user of the Tellus Sustain platform (the &quot;User&quot;). By clicking on
          &quot;Agree,&quot; you agree to be bound by the terms and conditions of this
          Agreement governing your access to, and use of, the Tellus Sustain
          Vendor Portal effective as of the date you click &quot;Agree&quot; (the
          &quot;Effective Date&quot;).
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">1. Scope of Agreement</h2>
        <p className="mb-4">
          Subject to the terms and conditions set forth in this Agreement, we
          agree to provide you with access to the Services. You acknowledge
          that the Services constitute an information-sharing venue only, and
          the Services shall not give rise to any fiduciary or equitable
          duties on our part.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">2. Term and Termination</h2>
        <p className="mb-4">
          (a) This Agreement is effective as of the Effective Date listed
          above and will continue in effect until terminated by either party
          at any time, with or without cause, upon delivery of written notice
          to the other party.
        </p>
        <p className="mb-4">
          (b) Regardless of any other provision of this Agreement, we have the
          right to suspend or terminate (at any time, with or without cause or
          prior notice) all or any part of the Services, or your access to the
          Services, and to change the nature, composition, or availability of
          the Services.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">3. Fees and Costs</h2>
        <p className="mb-4">
          (a) There are no fees or costs associated with your use of the Services.
        </p>
        <p className="mb-4">
          (b) You are responsible for all telecommunications and other
          connectivity costs, and the costs of any third-party software,
          equipment, and related maintenance services used by you to access
          the Services.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">4. Use of the Services</h2>
        <p className="mb-4">
          (a) We grant to you, for the term of this Agreement and subject to
          Section 7 hereof, a personal, limited, non-exclusive, revocable,
          non-transferable, and non-sublicenseable license to access and use
          the Services pursuant to the terms of this Agreement.
        </p>
        <p className="mb-4">
          (b) We are providing the Services to you only for your internal use,
          and only for the purposes set forth in this Agreement. You
          acknowledge that all proprietary rights in the Services are owned by
          us and our licensors and are protected under patent, copyright,
          trademark, trade secret, and other intellectual property rights and
          applicable law. No title to or ownership of the Services, nor any
          part thereof, is transferred to you. You receive no copyright,
          intellectual property rights, or other rights in or to the Services,
          except those specifically set forth in this Agreement. You agree not
          to reverse assemble, reverse compile, reverse engineer, or otherwise
          violate our proprietary rights in the Services, and you will honor
          and comply with our reasonable requests to protect our contractual,
          statutory, and common law rights in the Services.
        </p>
        <p className="mb-4">
          (c) You are bound by, and will comply with, our Terms of Use,
          Privacy Policy, and other terms, conditions, restrictions, legends,
          and disclaimers displayed on or linked to our internet website,
          which are incorporated herein by this reference (collectively, the
          &quot;Terms of Use&quot;). We shall have the right, at any time, to amend the
          provisions of our Terms of Use by posting them to our internet
          website or within the Services, or otherwise upon prior notice to
          you. In the event of any conflict between the Terms of Use and the
          terms of this Agreement, the terms of this Agreement shall control.
        </p>
        <p className="mb-4">
          (d) You may access the Services only through one or more passwords
          or other secured access methods that we specify (collectively,
          &quot;Access Methods&quot;). You are solely responsible for ensuring that your
          Access Methods are known to and used by only those individuals that
          you authorize pursuant to the Vendor Portal (&quot;Authorized Users&quot;),
          and you will provide us with updates of the Authorized Users
          promptly upon any changes thereto.
        </p>
        <p className="mb-4">
          (e) You will be solely responsible for all acts or omissions by any
          person using the Services through your Access Methods.
        </p>
        <p className="mb-4">
          (f) If your Access Methods have been lost, stolen, or compromised,
          you will promptly notify us. Upon receipt of this notice, your
          Access Methods will be canceled, but you are responsible for any
          actions taken through the use of such Access Methods before they are
          canceled. In our sole discretion, we may terminate, revoke, suspend,
          modify, or change any or all of your Access Methods at any time with
          or without prior notice.
        </p>
        <p className="mb-4">
          (g) You will be solely responsible for any losses, damages, or costs
          that you may incur as a result of errors made by, or the failure of,
          the hardware or software that you use to access the Services.
        </p>
        <p className="mb-4">
          (h) You will use the Services only for your own benefit and
          account(s) and will not use the Services on behalf of any third
          parties without our written permission, except for the User of
          Tellus Sustain that invited you to participate.
        </p>
        <p className="mb-4">
          (i) We have no responsibility for any communication that you submit
          using the Services that is inaccurate or incomplete.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">5. Representations and Warranties</h2>
        <p className="mb-4">
          (a) You represent and warrant to us that (i) all information
          submitted through the Vendor Portal is correct and complete in all
          material respects; (ii) your use of the Services will comply with
          the terms of this Agreement, our Terms of Use, and any other
          agreement between you and us; and (iii) you have all rights, power,
          and authority, and have obtained all necessary consents and
          approvals, to execute this Agreement, use the Services, and provide
          the information submitted through the Vendor Portal as set forth
          herein.
        </p>
        <p className="mb-4">
          (b) We represent and warrant to you that we have all rights, power,
          and authority, and have obtained all necessary consents and
          approvals, to execute this Agreement and provide the Service to you
          as set forth herein.
        </p>
        <p className="mb-4">
          (c) Except as set forth in Sections 5(b), 7, or 8, we make no
          representations or warranties, express or implied, to you concerning
          the Services. You expressly acknowledge and agree that we have made
          no recommendations with respect to the Services and that we provide
          the Services on an &quot;as is&quot; basis, at your sole risk. We expressly
          disclaim any implied warranties of merchantability or fitness for a
          particular purpose, including any warranties with respect to the
          correctness, quality, accuracy, completeness, reliability,
          performance, timeliness, or continued availability of the Services.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">6. Limitation of Liability; Indemnity</h2>
        <p className="mb-4">
          (a) We and our officers, directors, employees, agents, contractors,
          and affiliates (each a &quot;Related Party&quot;) have no liability,
          contingent or otherwise, to you or to third parties relating to the
          correctness, quality, accuracy, security, completeness, reliability,
          performance, timeliness, pricing, or continued availability of the
          Services, or for the failure of any connection or communication
          service to provide or maintain your access to the Services, or for
          any interruption, delay, or disruption of your access to the
          Services, or for any failure or inability to complete any activities
          undertaken through the Services, or for any erroneous communications
          between us and you. We and our Related Parties are not liable for
          any special, punitive, indirect, incidental, or consequential
          damages which you may incur or experience in connection with this
          Agreement or the Services, even if we know of the possibility of
          those damages. You are solely responsible for any losses, damages,
          or costs resulting from your reliance on any data or information
          that we may provide in connection with the Services, and we have no
          duty or obligation to verify, correct, complete, or update any data
          or information displayed in or transmitted using the Services.
        </p>
        <p className="mb-4">
          (b) You will indemnify, protect, and hold harmless us and our
          Related Parties from and against any and all losses, liabilities,
          judgments, suits, actions, proceedings, claims, damages, costs, and
          expenses, including reasonable attorney&apos;s fees (collectively,
          &quot;Losses&quot;), resulting from or arising out of any breach of the terms
          of this Agreement by you or your Related Parties, except to the
          extent such Losses are directly caused by our gross negligence or
          willful misconduct.
        </p>
        <p className="mb-4">
          (c) Information that you enter through the Services may be routed to
          third party systems, platforms, or services (each, a &quot;Third Party
          System&quot;). We are not responsible for any losses, damages, or costs
          that may result from errors made by any Third Party System in
          processing such information.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">7. Data and Information</h2>
        <p className="mb-4">
          (a) You have provided, or will provide, us with (i) all information
          and documents submitted in connection with this Agreement and your
          desire to use our Services, and (ii) all information we may
          reasonably request concerning you and your intended or actual use of
          the Services.
        </p>
        <p className="mb-4">
          (b) &quot;Confidential Information&quot; means all information, in any form,
          furnished or made available by one party (&quot;Disclosing Party&quot;) to the
          other party (&quot;Receiving Party&quot;) under this Agreement or in
          connection with the Services, including this Agreement, the Vendor
          Portal, and all information submitted using the Vendor Portal.
          Confidential Information does not include information that: (i) is
          or becomes known to the public through no fault of the Receiving
          Party; (ii) is already known to the Receiving Party prior to its
          receipt or becomes known to the Receiving Party by disclosure from a
          third party who has a lawful right to disclose the information;
          (iii) is known to the Receiving Party from its own independent
          development without reference to the Confidential Information of the
          Disclosing Party, as reasonably established by the Receiving Party;
          or (iv) is authorized to be disclosed by prior written consent of
          the Disclosing Party. The Receiving Party shall not use the
          Disclosing Party&apos;s Confidential Information for any purpose other
          than to perform its obligations under this Agreement and will not
          disclose any such Confidential Information to any third party,
          except that you authorize us to release your information submitted
          through the Vendor Portal to the User. The Receiving Party will take
          commercially reasonable steps to ensure that the Confidential
          Information is securely maintained in accordance with industry
          standards and in compliance with applicable law.
        </p>
        <p className="mb-4">
          (c) You hereby authorize us to derive anonymous usage data following
          your use of the Services and to compile, combine, and incorporate
          such anonymous data with or into other similar data and information
          available, derived, or obtained from not less than two other Users
          (collectively, &quot;Aggregate Data&quot;), provided that such Aggregate Data
          does not identify you or any Users as the source of the specified
          data and information. We shall be the owner of all Aggregate Data
          and, notwithstanding Paragraph 8(b) above, we shall have the right
          to use, manipulate, copy, disclose, publish, distribute, license, or
          sell the Aggregate Data to any third parties in our sole discretion.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">8. Electronic Documents</h2>
        <p className="mb-4">
          You will sign this Agreement, and may sign other documentation in
          electronic format through our web portal. You consent to the
          delivery of any required or optional communication under this
          Agreement, and any agreement or communication relating to the
          Services, by e-mail or other electronic means, subject to compliance
          with any applicable law. Any and all documents that are delivered to
          you electronically are deemed to be &quot;in writing.&quot; If your signature
          or acknowledgment is required or requested with respect to any such
          document and any Authorized User &quot;clicks&quot; in the appropriate space,
          or takes such other action as may be indicated on the Services, you
          will be deemed to have signed or acknowledged the document to the
          same extent and with the same effect as if you had signed the
          document manually. You acknowledge your understanding that you have
          the right to withdraw your consent to the future electronic delivery
          and signature of documents at any time by providing prior written
          notice. However, if you revoke your consent, your access to the
          Services may be restricted or terminated.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-4">9. General</h2>
        <p className="mb-4">
          (a) This Agreement contains the entire agreement of the parties with
          respect to its subject matter and supersedes all previous oral,
          written or other communications between the parties concerning the
          subject matter hereof. This Agreement may be modified only by a
          subsequent writing signed by all parties, except that additional
          terms and conditions applicable to the Services may be set forth on
          the Services.
        </p>
        <p className="mb-4">
          (b) Neither party may assign this Agreement without the other
          party&apos;s prior written consent. However, we may assign this Agreement
          without your consent to any entity (i) controlling, controlled by,
          or under common control with us, or (ii) which succeeds to all or
          substantially all of our assets and business.
        </p>
        <p className="mb-4">
          (c) If any provision of this Agreement (or any portion thereof) is
          invalid, illegal or unenforceable, the validity, legality or
          enforceability of the remainder of this Agreement will not be
          affected or impaired.
        </p>
        <p className="mb-4">
          (d) Where any form of the word &quot;including&quot; appears in this
          Agreement, it will be interpreted as if followed by the phrase
          &quot;without limitation&quot;.
        </p>
        <p className="mb-4">
          (e) Except as provided in Paragraph 9, all notices required or
          permitted hereunder will be in writing and delivered personally or
          by overnight courier, or forwarded by registered or certified mail,
          to the parties at the addresses listed on the first page, or to any
          other address which either party designates in writing to the other
          party after the date of this Agreement.
        </p>
        <p className="mb-4">
          (f) Neither party will have the right to use the names, logos, trade
          names or trademarks of the other unless such use is approved in
          advance in writing by the other party; provided, however, that we
          may include your name and logo in our user lists used for sales and
          marketing purposes, which shall not include use on public websites
          without permission.
        </p>
        <p className="mb-4">
          (g) Each party acknowledges that a breach of any provision of
          Paragraph 4 or 8 of this Agreement will cause the other irreparable
          injury and damage. Therefore, the non-breaching party shall be
          entitled to injunctive relief, without the necessity of posting a
          bond, in addition to any other rights and remedies which may be
          available to the party at law or in equity.
        </p>
        <p className="mb-4">
          (h) This Agreement will be governed and construed in all respects by
          the law of the State of New York, without giving effect to
          principles of conflict of law. Any dispute between the parties
          regarding any aspect of this Agreement, its formation, validity,
          interpretation, effect, performance, or breach (a &quot;Dispute&quot;), will
          be submitted to binding arbitration administered by the American
          Arbitration Association (&quot;AAA&quot;) in accordance with its comprehensive
          arbitration rules. Any such arbitration will be held in New York,
          New York, before a retired judge licensed to practice law in New
          York and selected in accordance with the procedures of AAA, or as
          otherwise agreed by the parties. The arbitrator&apos;s decision and/or
          award will be subject to an entry of judgment by any court having
          jurisdiction over the award. Except as provided in the following
          sentence or as otherwise provided by applicable law, if either party
          pursues any Dispute by any method other than arbitration, the
          responding party will recover from the initiating party all damages,
          costs, expenses and reasonable attorneys&apos; fees incurred as a result
          of such action. The parties agree that the only circumstance in
          which Disputes will not be subject to arbitration pursuant to this
          section is where a party makes a good faith determination that a
          breach or threatened breach of this Agreement by the other party is
          such that a temporary restraining order or other injunctive relief
          is the only appropriate and adequate remedy. The prevailing party in
          any arbitration or litigation proceedings arising out of this
          Agreement will have the right to recover its reasonable attorneys&apos;
          fees and costs from the other party.
        </p>
        <p className="mb-4">
          (i) Each party&apos;s continuing obligations under this Agreement,
          including those relating to &quot;Limitation of Liability&quot;, &quot;Indemnity&quot;
          and &quot;Data and Information&quot;, will survive the termination of this
          Agreement.
        </p>

        <p className="font-semibold mt-6 mb-4">
          IN WITNESS WHEREOF, the parties have executed this Agreement as of
          the Effective Date set forth above.
        </p>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button onClick={() => setIsOpen(false)}>Ok</Button>
      </div>
    </Modal>
  )
}
